import { State, Selector, Action, StateContext } from '@ngxs/store';
import {
  AddItemToCart,
  AddItineraryStop,
  AddItineraryStops,
  ClearItinerary,
  CreateItinerary,
  DeleteItinerary,
  FlashItinerary,
  LoadItinerary,
  LogoutAction,
  RemoveAllItemsFromCart,
  RemoveItemFromCart,
  RemoveItineraryStop,
  RemoveItineraryStops,
  RenameItinerary,
  ReorderItineraryStops,
  SaveItinerary,
  SelectItinerary,
  StartFromPersonaList,
  ToggleItineraryStop,
  UpdateItemInCart,
  UpdateProfileAction,
  UpdateSelectedEventMonth, UpdateSelectedMerchantCategory, UpdateSelectedNewsCategory, UpdateSelectedStoreCategory
} from './app.actions';
import { CartItem } from './app.models';
import { BootstrapGrowlService } from './components/growl/bootstrap-growl.service';
import { ToastrService } from 'ngx-toastr';
import { Injectable } from '@angular/core';
import { defaultNameForPersonaListId } from './pages/plan-your-visit-page/models/itinerary-persona-display';
import { copyStopRef, EMPTY_ITINERARY, itineraryStopKey, itineraryStopsEqual, ItineraryRecord, ItineraryState, ItineraryStop, MAX_ITINERARIES, itineraryUid } from './pages/plan-your-visit-page/models/itinerary.models';

export class ApplicationStateModel {
  public profile: any;
  public cart: CartItem[];
  public selectedEventMonth: string;
  public selectedMerchantCategory: string;
  public selectedMerchantSubCategory: string;
  public selectedStoreCategory: number | string;
  public selectedNewsCategory: string;
  public itineraries: ItineraryRecord[];
  public currentItineraryId: string | null;
  public flashItineraryId: string | null;
}

export const DEFAULT_ITINERARY_NAME_BASE = 'My Day';
const LEGACY_DEFAULT_ITINERARY_NAME_BASE = 'My Visit';

/** Returns the next default "My Day" / "My Day (2)" / ... name that isn't in use. */
export function nextDefaultItineraryName(list: ItineraryRecord[]): string {
  const used = new Set<string>();
  (list || []).forEach(rec => {
    const trimmed = (rec.name || '').trim();
    if (trimmed) { used.add(trimmed); }
  });
  if (!used.has(DEFAULT_ITINERARY_NAME_BASE)) {
    return DEFAULT_ITINERARY_NAME_BASE;
  }
  for (let i = 2; i < 1000; i++) {
    const candidate = `${DEFAULT_ITINERARY_NAME_BASE} (${i})`;
    if (!used.has(candidate)) { return candidate; }
  }
  return DEFAULT_ITINERARY_NAME_BASE;
}

/** True when a name is still an auto-assigned default ("My Day", "My Day (2)", …) or empty. */
export function isDefaultItineraryName(name: string | null | undefined): boolean {
  const trimmed = (name || '').trim();
  return trimmed === ''
    || trimmed === DEFAULT_ITINERARY_NAME_BASE
    || trimmed === LEGACY_DEFAULT_ITINERARY_NAME_BASE
    || new RegExp(`^${DEFAULT_ITINERARY_NAME_BASE} \\(\\d+\\)$`).test(trimmed)
    || new RegExp(`^${LEGACY_DEFAULT_ITINERARY_NAME_BASE} \\(\\d+\\)$`).test(trimmed);
}

@State<ApplicationStateModel>({
  name: 'app',
  defaults: {
    profile: null,
    cart: [],
    selectedEventMonth: null,
    selectedMerchantCategory: null,
    selectedMerchantSubCategory: null,
    selectedStoreCategory: null,
    selectedNewsCategory: null,
    itineraries: [],
    currentItineraryId: null,
    flashItineraryId: null
  } as ApplicationStateModel
})
@Injectable()
export class ApplicationStateHandlers {

  constructor(/*private toastr: ToastrService*/) {}

  @Selector()
  static profile(state: ApplicationStateModel): any {
    return state.profile;
  }

  @Selector()
  static cart(state: ApplicationStateModel): any {
    return state.cart;
  }

  @Selector()
  static selectedEventMonth(state: ApplicationStateModel): any {
    return state.selectedEventMonth;
  }

  @Selector()
  static selectedMerchantCategory(state: ApplicationStateModel): string {
    return state.selectedMerchantCategory;
  }

  @Selector()
  static selectedMerchantSubCategory(state: ApplicationStateModel): string {
    return state.selectedMerchantSubCategory;
  }

  @Selector()
  static selectedStoreCategory(state: ApplicationStateModel): string {
    return state.selectedStoreCategory as string;
  }

  @Selector()
  static selectedNewsCategory(state: ApplicationStateModel): string {
    return state.selectedNewsCategory;
  }

  @Selector()
  static itineraries(state: ApplicationStateModel): ItineraryRecord[] {
    return state.itineraries || [];
  }

  @Selector()
  static savedItineraries(state: ApplicationStateModel): ItineraryRecord[] {
    return (state.itineraries || []).filter(i => i.saved !== false);
  }

  @Selector()
  static hasSavedItineraries(state: ApplicationStateModel): boolean {
    return (state.itineraries || []).some(i => i.saved !== false);
  }

  @Selector()
  static currentItineraryIsSaved(state: ApplicationStateModel): boolean {
    const id = state.currentItineraryId;
    if (!id) { return false; }
    const record = (state.itineraries || []).find(i => i.id === id);
    return !!(record && record.saved !== false);
  }

  @Selector()
  static currentItineraryId(state: ApplicationStateModel): string | null {
    return state.currentItineraryId || null;
  }

  @Selector()
  static itinerary(state: ApplicationStateModel): ItineraryState {
    const list = state.itineraries || [];
    const id = state.currentItineraryId;
    const found = id ? list.find(i => i.id === id) : null;
    if (!found) {
      return EMPTY_ITINERARY;
    }
    const trimmed = (found.name || '').trim();
    if (trimmed !== '') {
      return trimmed === found.name ? found : { ...found, name: trimmed };
    }
    if (found.sourceList) {
      return { ...found, name: defaultNameForPersonaListId(found.sourceList) };
    }
    if (!found.name) {
      return found;
    }
    return { ...found, name: undefined };
  }

  @Selector()
  static itineraryStops(state: ApplicationStateModel): ItineraryStop[] {
    const list = state.itineraries || [];
    const id = state.currentItineraryId;
    const found = id ? list.find(i => i.id === id) : null;
    return (found && found.stops) || [];
  }

  @Selector()
  static itineraryCount(state: ApplicationStateModel): number {
    const list = state.itineraries || [];
    const id = state.currentItineraryId;
    const found = id ? list.find(i => i.id === id) : null;
    return (found && found.stops ? found.stops.length : 0);
  }

  @Selector()
  static flashItineraryId(state: ApplicationStateModel): string | null {
    return state.flashItineraryId || null;
  }

  /**
   * login
   */
  @Action(UpdateProfileAction)
  login({ getState, patchState }: StateContext<ApplicationStateModel>, { profile }: UpdateProfileAction) {
  //console.log('ApplicationState(): login()', profile);
    patchState({
      profile
    });
  }

  /**
   * logout
   */
  @Action(LogoutAction)
  logout({ getState, patchState }: StateContext<ApplicationStateModel>) {
  //console.log('ApplicationState(): logout()');
    patchState({
      profile: null
    });
  }

  /**
   * add item to cart
   */
  @Action(AddItemToCart)
  addItemToCart({ getState, patchState }: StateContext<ApplicationStateModel>, { item }: AddItemToCart) {
  //console.log('ApplicationState(): addItemToCart()', item, getState());
    let currentCart = getState().cart;
    if (!currentCart) {
      currentCart = [];
    }

    const existingItem = currentCart.find(o => {
      if (o.product.id === item.product.id && o.upc.upc === item.upc.upc) {
        return JSON.stringify(o.extra) === JSON.stringify(item.extra);
      }
      return false;
    });

    if (!!existingItem) {
      // update
      patchState({
        cart: currentCart.map(o => {
          if (o.product.id === item.product.id && o.upc.upc === item.upc.upc &&
              JSON.stringify(o.extra) === JSON.stringify(item.extra)) {

            // update this item with extra stuff
            const updated = Object.assign({}, o);
            updated.quantity += item.quantity;
            return updated;
          }
          return o;
        })
      });
      // this.toastr.success(`${item.product.name}${!!item.upc ? ' - ' + (!!item.upc.attribute_set ? item.upc.attribute_set.attribute_value.value : item.upc.upc) : ''} updated in cart`);
    } else {
      // add
      patchState({
        cart: currentCart.concat(item)
      });
      // this.toastr.success(`${item.product.name}${!!item.upc ? ' - ' + (!!item.upc.attribute_set ? item.upc.attribute_set.attribute_value.value : item.upc.upc) : ''} added to cart`);
    }
    // this.bootstrapGrowlService.addAlert(`${item.product.name} added to cart`, BootstrapAlertType.SUCCESS);
  }

  /**
   * remove item from cart
   */
  @Action(RemoveItemFromCart)
  removeItemFromCart({ getState, patchState }: StateContext<ApplicationStateModel>, { item }: RemoveItemFromCart) {
  //console.log('ApplicationState(): removeItemFromCart()', item);
    let currentCart = getState().cart;
    if (!currentCart) {
      currentCart = [];
    }

    patchState({
      cart: currentCart.filter(o => {
        if (o.product.id === item.product.id && ((!o.upc && !item.upc) || (!!o.upc && !!item.upc && o.upc.upc === item.upc.upc)) &&
            JSON.stringify(o.extra) === JSON.stringify(item.extra)) {
          return false;
        }
        return true;
      })
    });
  }

  /**
   * remove all items from cart
   */
  @Action(RemoveAllItemsFromCart)
  removeAllItemsFromCart({ getState, patchState }: StateContext<ApplicationStateModel>, { }: RemoveAllItemsFromCart) {
  //console.log('ApplicationState(): removeAllItemsFromCart()');
    patchState({
      cart: []
    });
  }

  /**
   * update item in cart
   */
  @Action(UpdateItemInCart)
  updateItemInCart({ getState, patchState }: StateContext<ApplicationStateModel>, { item, quantity }: UpdateItemInCart) {
  //console.log('ApplicationState(): updateItemInCart()', item);
    let currentCart = getState().cart;
    if (!currentCart) {
      currentCart = [];
    }

    const existingItem = currentCart.find(o => {
      if (o.product.id === item.product.id && o.upc.upc === item.upc.upc) {
        return JSON.stringify(o.extra) === JSON.stringify(item.extra);
      }
      return false;
    });

    if (quantity > 0) {
      patchState({
        cart: currentCart.map(o => {
          if (o === existingItem) {
            // update this item
            const newItem = Object.assign({}, item);
            newItem.quantity = quantity;
            return newItem;
          }
          return o;
        })
      });
    } else {
      // quantity 0, so just remove from the cart
      patchState({
        cart: getState().cart.filter(o => o !== existingItem)
      });
    }
  }

  /**
   * update selected event month
   */
  @Action(UpdateSelectedEventMonth)
  updateSelectedEventMonth({ getState, patchState }: StateContext<ApplicationStateModel>, { month }: UpdateSelectedEventMonth) {
  //console.log('ApplicationState(): updateSelectedEventMonth()');
    patchState({
      selectedEventMonth: month
    });
  }

  /**
   * update selected merchant category
   */
  @Action(UpdateSelectedMerchantCategory)
  updateSelectedMerchantCategory({ getState, patchState }: StateContext<ApplicationStateModel>, { categoryName, subCategoryName }: UpdateSelectedMerchantCategory) {
  //console.log('ApplicationState(): updateSelectedMerchantCategory()', categoryName, subCategoryName);
    patchState({
      selectedMerchantCategory: categoryName,
      selectedMerchantSubCategory: subCategoryName
    });
  }

  /**
   * update selected store category
   */
  @Action(UpdateSelectedStoreCategory)
  updateSelectedStoreCategory({ getState, patchState }: StateContext<ApplicationStateModel>, { categoryName }: UpdateSelectedStoreCategory) {
  //console.log('ApplicationState(): updateSelectedStoreCategory()');
    patchState({
      selectedStoreCategory: categoryName
    });
  }

  /**
   * update selected store category
   */
  @Action(UpdateSelectedNewsCategory)
  updateSelectedNewsCategory({ getState, patchState }: StateContext<ApplicationStateModel>, { categoryId }: UpdateSelectedNewsCategory) {
  //console.log('ApplicationState(): updateSelectedNewsCategory()');
    patchState({
      selectedNewsCategory: categoryId
    });
  }

  private ensureCurrent(state: ApplicationStateModel): { list: ItineraryRecord[]; currentId: string } {
    const list = (state.itineraries || []).slice();
    let currentId = state.currentItineraryId;
    if (!currentId || !list.find(i => i.id === currentId)) {
      const now = Date.now();
      const record: ItineraryRecord = {
        id: itineraryUid(),
        createdAt: now,
        name: undefined,
        stops: [],
        sourceList: null,
        updatedAt: now,
        saved: false
      };
      list.push(record);
      currentId = record.id;
    }
    return { list, currentId };
  }

  private updateRecord(list: ItineraryRecord[], id: string, mutator: (record: ItineraryRecord) => ItineraryRecord): ItineraryRecord[] {
    return list.map(i => (i.id === id ? mutator(i) : i));
  }

  private markSavedOnFirstStop(rec: ItineraryRecord): boolean {
    return rec.saved || rec.stops.length === 0;
  }

  @Action(CreateItinerary)
  createItinerary({ getState, patchState }: StateContext<ApplicationStateModel>, { name, setAsCurrent }: CreateItinerary) {
    const list = (getState().itineraries || []).slice();
    if (list.length >= MAX_ITINERARIES) {
      return;
    }
    const now = Date.now();
    const trimmed = (name || '').trim();
    const resolvedName = trimmed !== '' ? trimmed : nextDefaultItineraryName(list);
    const record: ItineraryRecord = {
      id: itineraryUid(),
      createdAt: now,
      name: resolvedName,
      stops: [],
      sourceList: null,
      updatedAt: now,
      saved: true
    };
    list.push(record);
    patchState({
      itineraries: list,
      ...(setAsCurrent !== false ? { currentItineraryId: record.id } : {})
    });
  }

  @Action(FlashItinerary)
  flashItinerary({ patchState }: StateContext<ApplicationStateModel>, { id }: FlashItinerary) {
    patchState({ flashItineraryId: id });
  }

  @Action(SelectItinerary)
  selectItinerary({ getState, patchState }: StateContext<ApplicationStateModel>, { id }: SelectItinerary) {
    if (id === null) {
      patchState({ currentItineraryId: null });
      return;
    }
    const list = getState().itineraries || [];
    if (list.find(i => i.id === id)) {
      patchState({ currentItineraryId: id });
    }
  }

  @Action(DeleteItinerary)
  deleteItinerary({ getState, patchState }: StateContext<ApplicationStateModel>, { id }: DeleteItinerary) {
    let list = (getState().itineraries || []).filter(i => i.id !== id);
    const stillCurrent = getState().currentItineraryId === id ? null : getState().currentItineraryId;
    let nextCurrent = stillCurrent !== null ? stillCurrent : (list.length > 0 ? list[list.length - 1].id : null);

    if (list.length === 0) {
      const now = Date.now();
      const record: ItineraryRecord = {
        id: itineraryUid(),
        createdAt: now,
        name: nextDefaultItineraryName([]),
        stops: [],
        sourceList: null,
        updatedAt: now,
        saved: true
      };
      list = [record];
      nextCurrent = record.id;
    }

    patchState({ itineraries: list, currentItineraryId: nextCurrent });
  }

  @Action(AddItineraryStop)
  addItineraryStop({ getState, patchState }: StateContext<ApplicationStateModel>, { stop }: AddItineraryStop) {
    const { list, currentId } = this.ensureCurrent(getState());
    const now = Date.now();
    const nextList = this.updateRecord(list, currentId, rec => {
      if (rec.stops.some(s => itineraryStopsEqual(s, stop))) { return rec; }
      return Object.assign({}, rec, {
        stops: rec.stops.concat({ ...copyStopRef(stop), addedAt: now }),
        saved: this.markSavedOnFirstStop(rec),
        updatedAt: now
      });
    });
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }

  @Action(AddItineraryStops)
  addItineraryStops({ getState, patchState }: StateContext<ApplicationStateModel>, { stops }: AddItineraryStops) {
    if (!stops || stops.length === 0) { return; }
    const { list, currentId } = this.ensureCurrent(getState());
    const now = Date.now();
    const nextList = this.updateRecord(list, currentId, rec => {
      const existingKeys = new Set(rec.stops.map(s => itineraryStopKey(s)));
      const additions = stops
        .filter(s => !existingKeys.has(itineraryStopKey(s)))
        .map(s => ({ ...copyStopRef(s), addedAt: now }));
      if (additions.length === 0) { return rec; }
      return Object.assign({}, rec, {
        stops: rec.stops.concat(additions),
        saved: this.markSavedOnFirstStop(rec),
        updatedAt: now
      });
    });
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }

  @Action(RemoveItineraryStop)
  removeItineraryStop({ getState, patchState }: StateContext<ApplicationStateModel>, { stop }: RemoveItineraryStop) {
    const { list, currentId } = this.ensureCurrent(getState());
    const now = Date.now();
    const nextList = this.updateRecord(list, currentId, rec => Object.assign({}, rec, {
      stops: rec.stops.filter(s => !itineraryStopsEqual(s, stop)),
      updatedAt: now
    }));
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }

  @Action(RemoveItineraryStops)
  removeItineraryStops({ getState, patchState }: StateContext<ApplicationStateModel>, { stops }: RemoveItineraryStops) {
    if (!stops || stops.length === 0) { return; }
    const { list, currentId } = this.ensureCurrent(getState());
    const removeKeys = new Set(stops.map(s => itineraryStopKey(s)));
    const now = Date.now();
    const nextList = this.updateRecord(list, currentId, rec => {
      const nextStops = rec.stops.filter(s => !removeKeys.has(itineraryStopKey(s)));
      if (nextStops.length === rec.stops.length) { return rec; }
      return Object.assign({}, rec, { stops: nextStops, updatedAt: now });
    });
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }

  @Action(ToggleItineraryStop)
  toggleItineraryStop({ getState, patchState }: StateContext<ApplicationStateModel>, { stop }: ToggleItineraryStop) {
    const { list, currentId } = this.ensureCurrent(getState());
    const now = Date.now();
    const nextList = this.updateRecord(list, currentId, rec => {
      const exists = rec.stops.some(s => itineraryStopsEqual(s, stop));
      const nextStops = exists
        ? rec.stops.filter(s => !itineraryStopsEqual(s, stop))
        : rec.stops.concat({ ...copyStopRef(stop), addedAt: now });
      return Object.assign({}, rec, {
        stops: nextStops,
        saved: exists ? rec.saved : this.markSavedOnFirstStop(rec),
        updatedAt: now
      });
    });
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }

  @Action(ReorderItineraryStops)
  reorderItineraryStops({ getState, patchState }: StateContext<ApplicationStateModel>, { stops }: ReorderItineraryStops) {
    const { list, currentId } = this.ensureCurrent(getState());
    const nextList = this.updateRecord(list, currentId, rec => Object.assign({}, rec, {
      stops: stops.slice(),
      updatedAt: Date.now()
    }));
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }

  @Action(RenameItinerary)
  renameItinerary({ getState, patchState }: StateContext<ApplicationStateModel>, { name }: RenameItinerary) {
    const { list, currentId } = this.ensureCurrent(getState());
    const nextList = this.updateRecord(list, currentId, rec => Object.assign({}, rec, {
      name,
      updatedAt: Date.now()
    }));
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }

  @Action(ClearItinerary)
  clearItinerary({ getState, patchState }: StateContext<ApplicationStateModel>) {
    const current = getState().currentItineraryId;
    if (!current) { return; }
    const list = (getState().itineraries || []).map(rec => rec.id === current
      ? Object.assign({}, rec, { stops: [], updatedAt: Date.now() })
      : rec);
    patchState({ itineraries: list });
  }

  @Action(LoadItinerary)
  loadItinerary({ getState, patchState }: StateContext<ApplicationStateModel>, { state }: LoadItinerary) {
    const list = (getState().itineraries || []).slice();
    const now = Date.now();
    const record: ItineraryRecord = {
      id: itineraryUid(),
      createdAt: now,
      name: state.name,
      stops: (state.stops || []).slice(),
      sourceList: state.sourceList || null,
      updatedAt: now,
      saved: true
    };
    list.push(record);
    patchState({ itineraries: list, currentItineraryId: record.id });
  }

  @Action(StartFromPersonaList)
  startFromPersonaList({ getState, patchState }: StateContext<ApplicationStateModel>, { listId, stops }: StartFromPersonaList) {
    const list = (getState().itineraries || []).slice();
    const now = Date.now();
    const record: ItineraryRecord = {
      id: itineraryUid(),
      createdAt: now,
      name: defaultNameForPersonaListId(listId),
      sourceList: listId,
      stops: stops.map(s => ({ ...copyStopRef(s), addedAt: now })),
      updatedAt: now,
      saved: true
    };
    list.push(record);
    patchState({ itineraries: list, currentItineraryId: record.id });
  }

  @Action(SaveItinerary)
  saveItinerary({ getState, patchState }: StateContext<ApplicationStateModel>, { name }: SaveItinerary) {
    const { list, currentId } = this.ensureCurrent(getState());
    const now = Date.now();
    const nextList = this.updateRecord(list, currentId, rec => {
      let resolved: string | undefined;
      if (name !== undefined) {
        const t = (name || '').trim();
        resolved = t !== '' ? t : undefined;
      } else {
        const fromRecord = (rec.name || '').trim();
        resolved = fromRecord !== '' ? fromRecord : undefined;
      }
      if (!resolved && rec.sourceList) {
        resolved = defaultNameForPersonaListId(rec.sourceList);
      }
      return Object.assign({}, rec, {
        saved: true,
        name: resolved,
        updatedAt: now
      });
    });
    patchState({ itineraries: nextList, currentItineraryId: currentId });
  }
}
